import React, { useState, useEffect } from 'react';
import { Button, Modal, Form, Alert, Spinner, Container } from 'react-bootstrap';
import { machineService } from '../services/api';
import MachineTable from '../components/MachineTable';
import ClusterManager from '../components/ClusterManager';
import ScreenMirrorModal from '../components/ScreenMirrorModal';
import KioskControlModal from '../components/KioskControlModal';
import { useAuth } from '../contexts/AuthContext';

function MachinesPage() {
  const { user, hasPermission } = useAuth();
  const [machines, setMachines] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showClusterModal, setShowClusterModal] = useState(false);
  const [showMirrorModal, setShowMirrorModal] = useState(false);
  const [mirrorMachine, setMirrorMachine] = useState(null);
  const [showKioskModal, setShowKioskModal] = useState(false);
  const [kioskMachine, setKioskMachine] = useState(null);
  const [editingMachine, setEditingMachine] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [newMachine, setNewMachine] = useState({
    name: '',
    mac_address: '',
    ip_address: '',
    subnet_mask: '255.255.255.0',
    broadcast_address: '',
    username: '',
    password: '',
    ssh_port: 22
  });
  const [alert, setAlert] = useState({ show: false, message: '', variant: 'success' });
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchMachines();
  }, []);

  const fetchMachines = async () => {
    setLoading(true);
    try {
      const data = await machineService.getAll();
      setMachines(data);
    } catch (err) {
      showAlert('Failed to fetch machines', 'danger');
    } finally {
      setLoading(false);
    }
  };

  const isValidIPv4 = (ip) => {
    if (!ip || typeof ip !== 'string') return false;
    const trimmed = ip.trim();
    const ipv4Pattern = /^(25[0-5]|2[0-4]\d|1?\d?\d)(\.(25[0-5]|2[0-4]\d|1?\d?\d)){3}$/;
    return ipv4Pattern.test(trimmed);
  };

  const isValidMAC = (mac) => {
    if (!mac || typeof mac !== 'string') return false;
    const trimmed = mac.trim();
    const macPattern = /^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$/;
    const plainPattern = /^[0-9A-Fa-f]{12}$/;
    return macPattern.test(trimmed) || plainPattern.test(trimmed);
  };

  const normalizeMAC = (mac) => {
    if (!mac) return mac;
    const trimmed = mac.trim();
    const plainPattern = /^[0-9A-Fa-f]{12}$/;
    if (plainPattern.test(trimmed)) {
      return trimmed.match(/.{1,2}/g).join(':').toLowerCase();
    }
    return trimmed.replace(/-/g, ':').toLowerCase();
  };

  const handleAddMachine = async () => {
    if (!newMachine.name.trim()) {
      showAlert('Please enter a machine name', 'danger');
      return;
    }
    if (!newMachine.mac_address.trim()) {
      showAlert('Please enter a MAC address', 'danger');
      return;
    }
    if (!isValidMAC(newMachine.mac_address)) {
      showAlert('Please enter a valid MAC address (e.g. 00:11:22:33:44:55)', 'danger');
      return;
    }
    if (!newMachine.ip_address.trim()) {
      showAlert('Please enter an IP address', 'danger');
      return;
    }
    if (!isValidIPv4(newMachine.ip_address)) {
      showAlert('Please enter a valid IPv4 address (e.g. 192.168.1.10)', 'danger');
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        ...newMachine,
        mac_address: normalizeMAC(newMachine.mac_address),
        ssh_port: newMachine.ssh_port !== undefined && newMachine.ssh_port !== '' ? Number(newMachine.ssh_port) : 22
      };
      await machineService.create(payload);
      setShowAddModal(false);
      setNewMachine({
        name: '',
        mac_address: '',
        ip_address: '',
        subnet_mask: '255.255.255.0',
        broadcast_address: '',
        username: '',
        password: '',
        ssh_port: 22
      });
      fetchMachines();
      showAlert('Machine added successfully! 🎉', 'success');
    } catch (err) {
      console.error('Add machine error:', err);
      showAlert('Failed to add machine. Please try again.', 'danger');
    } finally {
      setIsLoading(false);
    }
  };

  const handleMachineAction = async (machineIds, actionType) => {
    if (actionType === 'mirror') {
      const target = machines.find(m => m.id === machineIds[0]);
      if (target) {
        setMirrorMachine(target);
        setShowMirrorModal(true);
      }
      return;
    }
    if (actionType === 'kiosk') {
      const target = machines.find(m => m.id === machineIds[0]);
      if (target) {
        setKioskMachine(target);
        setShowKioskModal(true);
      }
      return;
    }
    try {
      await machineService.clusterAction(machineIds, actionType);
      showAlert(`${actionType} command sent successfully`, 'success');
      fetchMachines();
    } catch (err) {
      showAlert(`Failed to perform ${actionType}`, 'danger');
    }
  };

  const handleEditMachine = (machine) => {
    setEditingMachine(machine);
    setNewMachine({
      name: machine.name,
      mac_address: machine.mac_address,
      ip_address: machine.ip_address,
      subnet_mask: machine.subnet_mask,
      broadcast_address: machine.broadcast_address,
      username: machine.username,
      password: '',
      ssh_port: machine.ssh_port !== undefined ? machine.ssh_port : 22
    });
    setShowEditModal(true);
  };

  const handleUpdateMachine = async () => {
    if (!newMachine.name.trim()) {
      showAlert('Please enter a machine name', 'danger');
      return;
    }
    if (!newMachine.mac_address.trim()) {
      showAlert('Please enter a MAC address', 'danger');
      return;
    }
    if (!isValidMAC(newMachine.mac_address)) {
      showAlert('Please enter a valid MAC address (e.g. 00:11:22:33:44:55)', 'danger');
      return;
    }
    if (!newMachine.ip_address.trim()) {
      showAlert('Please enter an IP address', 'danger');
      return;
    }
    if (!isValidIPv4(newMachine.ip_address)) {
      showAlert('Please enter a valid IPv4 address (e.g. 192.168.1.10)', 'danger');
      return;
    }

    setIsUpdating(true);
    try {
      const payload = {
        ...newMachine,
        mac_address: normalizeMAC(newMachine.mac_address),
        ssh_port: newMachine.ssh_port !== undefined && newMachine.ssh_port !== '' ? Number(newMachine.ssh_port) : 22
      };
      if (!newMachine.password) {
        delete payload.password;
      }
      await machineService.update(editingMachine.id, payload);
      setShowEditModal(false);
      setEditingMachine(null);
      setNewMachine({
        name: '',
        mac_address: '',
        ip_address: '',
        subnet_mask: '255.255.255.0',
        broadcast_address: '',
        username: '',
        password: '',
        ssh_port: 22
      });
      fetchMachines();
      showAlert('Machine updated successfully! ✨', 'success');
    } catch (err) {
      console.error('Update machine error:', err);
      showAlert('Failed to update machine. Please try again.', 'danger');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteMachine = async (machineId) => {
    if (window.confirm('⚠️ Are you sure you want to delete this machine? This action cannot be undone.')) {
      try {
        await machineService.delete(machineId);
        fetchMachines();
        showAlert('Machine deleted successfully! 🗑️', 'success');
      } catch (err) {
        console.error('Delete machine error:', err);
        showAlert('Failed to delete machine. Please try again.', 'danger');
      }
    }
  };

  const handleDownloadClientZip = async () => {
    try {
      const blob = await machineService.downloadClientZip();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'client-service.zip');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showAlert('Client service downloaded successfully! 📦', 'success');
    } catch (err) {
      console.error('Download failed:', err);
      showAlert('Failed to download client service zip. Please try again.', 'danger');
    }
  };

  const showAlert = (message, variant) => {
    setAlert({ show: true, message, variant });
    setTimeout(() => setAlert({ show: false, message: '', variant: 'success' }), 5000);
  };

  const filteredMachines = machines.filter(machine => {
    const matchesSearch = 
      machine.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      machine.ip_address.toLowerCase().includes(searchTerm.toLowerCase()) ||
      machine.mac_address.toLowerCase().includes(searchTerm.toLowerCase());

    let matchesStatus = true;
    if (statusFilter === 'connected') {
      matchesStatus = machine.connection_status === 'connected';
    } else if (statusFilter === 'disconnected') {
      matchesStatus = machine.connection_status !== 'connected';
    } else if (statusFilter === 'online') {
      matchesStatus = machine.is_active === 1;
    } else if (statusFilter === 'offline') {
      matchesStatus = machine.is_active !== 1;
    }

    return matchesSearch && matchesStatus;
  });

  return (
    <div className="py-4" style={{ minHeight: '100vh', paddingLeft: '2rem', paddingRight: '2rem' }}>
      <Container fluid className="px-0">

        {/* Header Section */}
        <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
          <div>
            <h1 className="mb-1 text-theme-main" style={{ fontSize: '1.75rem', fontWeight: 700 }}>
              <i className="fas fa-desktop me-2 text-primary"></i>
              Machine Management
            </h1>
            <p className="text-theme-muted mb-0" style={{ fontSize: '0.9rem' }}>
              Add, manage and remotely monitor your local network assets
            </p>
          </div>
          {hasPermission('manage_assets') && (
            <div className="d-flex gap-2">
              {user?.role === 'admin' && (
                <Button
                  onClick={handleDownloadClientZip}
                  className="btn-premium-accent d-flex align-items-center gap-2"
                >
                  <i className="fas fa-download"></i>
                  Download Agent Service
                </Button>
              )}
              <Button
                onClick={() => setShowAddModal(true)}
                className="btn-premium-primary d-flex align-items-center gap-2"
              >
                <i className="fas fa-plus"></i>
                Add Machine
              </Button>
              <Button
                onClick={() => setShowClusterModal(true)}
                className="btn-premium-secondary d-flex align-items-center gap-2"
              >
                <i className="fas fa-layer-group"></i>
                Manage Clusters
              </Button>
            </div>
          )}
        </div>

        {/* Alert Section */}
        {alert.show && (
          <Alert
            variant={alert.variant}
            onClose={() => setAlert({ ...alert, show: false })}
            dismissible
            className="mb-4 border-0"
            style={{
              backgroundColor: alert.variant === 'success' ? 'rgba(16, 185, 129, 0.12)' : 'rgba(244, 63, 94, 0.12)',
              color: alert.variant === 'success' ? 'var(--color-success)' : 'var(--color-danger)',
              borderRadius: 'var(--radius-md)'
            }}
          >
            <i className={`fas ${alert.variant === 'success' ? 'fa-check-circle' : 'fa-exclamation-triangle'} me-2`}></i>
            {alert.message}
          </Alert>
        )}

        {/* Stats and Search bar */}
        <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
          <div>
            <h4 className="text-theme-main mb-1" style={{ fontSize: '1.1rem', fontWeight: 600 }}>
              {filteredMachines.length} of {machines.length} Machines
            </h4>
            <p className="text-theme-muted mb-0" style={{ fontSize: '0.85rem' }}>
              <span className="text-emerald-400 font-semibold">{machines.filter(m => m.is_active === 1).length} active</span> • {machines.filter(m => m.is_active === 0).length} inactive
            </p>
          </div>

          <div className="d-flex align-items-center gap-3">
            <div className="position-relative">
              <i className="fas fa-search position-absolute text-muted"
                style={{ left: '12px', top: '50%', transform: 'translateY(-50%)', fontSize: '0.85rem' }}
              ></i>
              <Form.Control
                type="text"
                placeholder="Search by name, IP, or MAC..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="form-input-premium form-input-search-premium"
                style={{ width: '280px', fontSize: '0.875rem' }}
              />
            </div>
            <Form.Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="form-input-premium py-1"
              style={{ width: '180px', fontSize: '0.875rem' }}
            >
              <option value="all">All Machines</option>
              <option value="connected">Agent: Connected</option>
              <option value="disconnected">Agent: Disconnected</option>
              <option value="online">Power: Online</option>
              <option value="offline">Power: Offline</option>
            </Form.Select>
          </div>
        </div>

        {/* Content Section */}
        {loading ? (
          <div className="glass-card text-center py-5">
            <Spinner animation="border" variant="primary" className="mb-3" />
            <p className="text-muted mb-0">Loading machines...</p>
          </div>
        ) : (
          <div className="glass-card">
            <MachineTable
              machines={filteredMachines}
              onEdit={handleEditMachine}
              onDelete={handleDeleteMachine}
              onAction={handleMachineAction}
            />
          </div>
        )}

        {/* Add Machine Modal */}
        <Modal show={showAddModal} onHide={() => setShowAddModal(false)} size="lg" centered>
          <div className="modal-content-premium">
            <Modal.Header closeButton className="modal-header-premium border-0">
              <Modal.Title style={{ fontWeight: 700 }}>
                <i className="fas fa-plus me-2 text-primary"></i>
                Add New Machine
              </Modal.Title>
            </Modal.Header>
            <Modal.Body className="px-4 py-3">
              <Form>
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <Form.Label className="form-label-premium text-uppercase tracking-wider">Machine Name</Form.Label>
                    <Form.Control
                      type="text"
                      className="form-input-premium w-100"
                      placeholder="e.g. Workstation-01"
                      value={newMachine.name}
                      onChange={(e) => setNewMachine({ ...newMachine, name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <Form.Label className="form-label-premium text-uppercase tracking-wider">MAC Address</Form.Label>
                    <Form.Control
                      type="text"
                      className="form-input-premium w-100"
                      placeholder="e.g. 00:11:22:33:44:55"
                      value={newMachine.mac_address}
                      onChange={(e) => setNewMachine({ ...newMachine, mac_address: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6 mb-3">
                    <Form.Label className="form-label-premium text-uppercase tracking-wider">IP Address</Form.Label>
                    <Form.Control
                      type="text"
                      className="form-input-premium w-100"
                      placeholder="e.g. 192.168.1.50"
                      value={newMachine.ip_address}
                      onChange={(e) => setNewMachine({ ...newMachine, ip_address: e.target.value })}
                      required
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <Form.Label className="form-label-premium text-uppercase tracking-wider">Subnet Mask</Form.Label>
                    <Form.Control
                      type="text"
                      className="form-input-premium w-100"
                      placeholder="255.255.255.0"
                      value={newMachine.subnet_mask}
                      onChange={(e) => setNewMachine({ ...newMachine, subnet_mask: e.target.value })}
                    />
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-12 mb-3">
                    <Form.Label className="form-label-premium text-uppercase tracking-wider">Broadcast Address</Form.Label>
                    <Form.Control
                      type="text"
                      className="form-input-premium w-100"
                      placeholder="e.g. 192.168.1.255"
                      value={newMachine.broadcast_address}
                      onChange={(e) => setNewMachine({ ...newMachine, broadcast_address: e.target.value })}
                    />
                  </div>
                </div>
              </Form>
            </Modal.Body>
            <Modal.Footer className="modal-footer-premium border-0 gap-2">
              <Button className="btn-premium-secondary" onClick={() => setShowAddModal(false)}>
                Cancel
              </Button>
              <Button className="btn-premium-primary" onClick={handleAddMachine} disabled={isLoading}>
                {isLoading ? <Spinner size="sm" animation="border" /> : 'Save Machine'}
              </Button>
            </Modal.Footer>
          </div>
        </Modal>

        {/* Edit Machine Modal */}
        <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg" centered>
          <div className="modal-content-premium">
            <Modal.Header closeButton className="modal-header-premium border-0">
              <Modal.Title style={{ fontWeight: 700 }}>
                <i className="fas fa-edit me-2 text-primary"></i>
                Edit Machine
              </Modal.Title>
            </Modal.Header>
            <Modal.Body className="px-4 py-3">
              <Form>
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <Form.Label className="form-label-premium text-uppercase tracking-wider">Machine Name</Form.Label>
                    <Form.Control
                      type="text"
                      className="form-input-premium w-100"
                      value={newMachine.name}
                      onChange={(e) => setNewMachine({ ...newMachine, name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <Form.Label className="form-label-premium text-uppercase tracking-wider">MAC Address</Form.Label>
                    <Form.Control
                      type="text"
                      className="form-input-premium w-100"
                      value={newMachine.mac_address}
                      onChange={(e) => setNewMachine({ ...newMachine, mac_address: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6 mb-3">
                    <Form.Label className="form-label-premium text-uppercase tracking-wider">IP Address</Form.Label>
                    <Form.Control
                      type="text"
                      className="form-input-premium w-100"
                      value={newMachine.ip_address}
                      onChange={(e) => setNewMachine({ ...newMachine, ip_address: e.target.value })}
                      required
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <Form.Label className="form-label-premium text-uppercase tracking-wider">Subnet Mask</Form.Label>
                    <Form.Control
                      type="text"
                      className="form-input-premium w-100"
                      value={newMachine.subnet_mask}
                      onChange={(e) => setNewMachine({ ...newMachine, subnet_mask: e.target.value })}
                    />
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-12 mb-3">
                    <Form.Label className="form-label-premium text-uppercase tracking-wider">Broadcast Address</Form.Label>
                    <Form.Control
                      type="text"
                      className="form-input-premium w-100"
                      value={newMachine.broadcast_address}
                      onChange={(e) => setNewMachine({ ...newMachine, broadcast_address: e.target.value })}
                    />
                  </div>
                </div>
              </Form>
            </Modal.Body>
            <Modal.Footer className="modal-footer-premium border-0 gap-2">
              <Button className="btn-premium-secondary" onClick={() => setShowEditModal(false)}>
                Cancel
              </Button>
              <Button className="btn-premium-primary" onClick={handleUpdateMachine} disabled={isUpdating}>
                {isUpdating ? <Spinner size="sm" animation="border" /> : 'Update Machine'}
              </Button>
            </Modal.Footer>
          </div>
        </Modal>

        {/* Cluster Manager Modal */}
        <ClusterManager
          show={showClusterModal}
          onHide={() => setShowClusterModal(false)}
          machines={machines}
          onClusterCreated={fetchMachines}
        />

        {/* Screen Mirror Modal */}
        <ScreenMirrorModal
          show={showMirrorModal}
          onHide={() => { setShowMirrorModal(false); setMirrorMachine(null); }}
          machine={mirrorMachine}
        />

        {/* Kiosk Mode Control Modal */}
        <KioskControlModal
          show={showKioskModal}
          onHide={() => { setShowKioskModal(false); setKioskMachine(null); }}
          machine={kioskMachine}
          onSuccess={fetchMachines}
        />

      </Container>
    </div>
  );
}

export default MachinesPage;